import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

// docs/CAPACITY_CREDITS_DESIGN.md §9 — superadmin grants to admins (minting,
// unconstrained), admin grants to its own end-user broker accounts (spends
// from its own wallet). Both roles land on this same page.

const EMPTY_GRANT = { recipient_username: "", credits: "", validity_days: "30", note: "" };

export default function CreditManagementPage() {
  const { user: sessionUser } = useAuth();
  const isSuperAdmin = sessionUser?.user_type === "superadmin";

  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [recipients, setRecipients] = useState([]);
  const [granterBalance, setGranterBalance] = useState(null); // null = unlimited (superadmin)

  const [grantDraft, setGrantDraft] = useState(EMPTY_GRANT);
  const [granting, setGranting] = useState(false);

  const [detailUsername, setDetailUsername] = useState(null);
  const [detail, setDetail] = useState(null);

  // A previous toast's dismiss-timer firing after a newer toast was set would
  // wipe the newer one out early (classic stale-setTimeout race) — track the
  // pending timer so each flash() cancels whatever it was about to replace.
  const dismissTimer = useRef(null);
  const flash = (text, ok = true) => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    setMsg({ text, ok });
    dismissTimer.current = setTimeout(() => setMsg(null), ok ? 3500 : 6000);
  };

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/credits/wallets");
      setWallets(res.data);
    } catch {
      flash("Failed to load wallets.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecipients = useCallback(async () => {
    try {
      const res = await axios.get("/admin/credits/recipients");
      setRecipients(res.data.recipients || []);
      setGranterBalance(res.data.granter_balance);
    } catch {
      flash("Failed to load recipient list.", false);
    }
  }, []);

  useEffect(() => { fetchWallets(); fetchRecipients(); }, [fetchWallets, fetchRecipients]);

  const openDetail = async (username) => {
    setDetailUsername(username);
    setDetail(null);
    try {
      const res = await axios.get(`/admin/credits/wallet/${encodeURIComponent(username)}`);
      setDetail(res.data);
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to load wallet detail.", false);
      setDetailUsername(null);
    }
  };

  const submitGrant = async () => {
    if (!grantDraft.recipient_username || !grantDraft.credits || !grantDraft.validity_days) {
      flash("Recipient, credits and validity days are required.", false);
      return;
    }
    const requested = Number(grantDraft.credits);
    // Client-side check for the common case — the backend still enforces this
    // authoritatively (a concurrent grant could change the balance between
    // this check and the request landing), so this is a fast-feedback UX
    // guard, not the source of truth.
    if (granterBalance !== null && requested > granterBalance) {
      flash(`Cannot grant ${requested} credits — your wallet only has ${granterBalance} remaining.`, false);
      return;
    }
    setGranting(true);
    try {
      await axios.post("/admin/credits/grant", {
        recipient_username: grantDraft.recipient_username.trim(),
        credits: requested,
        validity_days: Number(grantDraft.validity_days),
        note: grantDraft.note || undefined,
      });
      flash(`Granted ${grantDraft.credits} credits to ${grantDraft.recipient_username}.`);
      setGrantDraft(EMPTY_GRANT);
      fetchWallets();
      fetchRecipients(); // refresh granter's own balance after spending from it
      if (detailUsername === grantDraft.recipient_username.trim()) openDetail(detailUsername);
    } catch (e) {
      // Backend's ValueError message (e.g. "Cannot grant N credits — your
      // wallet only has M remaining.") — surfaced verbatim so an admin who
      // hits this from a stale client-side balance still sees why it failed.
      flash(e?.response?.data?.error || "Grant failed.", false);
    } finally {
      setGranting(false);
    }
  };

  const statusBadge = (balance, threshold) => {
    if (balance <= 0) return { label: "Exhausted", color: "#dc3545", bg: "#fde2e2" };
    if (balance < threshold) return { label: "Low", color: "#fd7e14", bg: "#fff3cd" };
    return { label: "OK", color: "#28a745", bg: "#d4edda" };
  };

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>
      {msg && createPortal(
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text || "(no message)"}
        </div>,
        document.body
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Capacity Credits</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          {isSuperAdmin
            ? "Grant capacity credits to admin wallets. Superadmin credits are minted, not spent from a wallet."
            : "Grant capacity credits to your end users' broker accounts, spent from your own wallet."}
        </p>
      </div>

      {/* Grant form */}
      <div style={{ ...tableCard, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#2c3e50" }}>
            {isSuperAdmin ? "Grant to Admin" : "Grant to My Users"}
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            {granterBalance === null
              ? "Unlimited — superadmin credits are minted, not spent from a wallet."
              : <>Your available balance: <strong style={{ color: granterBalance > 0 ? "#2c3e50" : "#dc3545" }}>{granterBalance}</strong> credits</>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 1.4fr auto", gap: 10, alignItems: "end" }}>
          <div>
            <div style={formLabel}>{isSuperAdmin ? "Admin *" : "Account *"}</div>
            <select
              value={grantDraft.recipient_username}
              onChange={e => setGrantDraft(d => ({ ...d, recipient_username: e.target.value }))}
              style={formInput}
            >
              <option value="">— select —</option>
              {recipients.map(r => (
                <option key={r.username} value={r.username}>{r.label}</option>
              ))}
            </select>
          </div>
          <LabeledInput label="Credits *" type="number" value={grantDraft.credits}
            onChange={v => setGrantDraft(d => ({ ...d, credits: v }))} />
          <LabeledInput label="Validity (days) *" type="number" value={grantDraft.validity_days}
            onChange={v => setGrantDraft(d => ({ ...d, validity_days: v }))} />
          <LabeledInput label="Note" value={grantDraft.note}
            onChange={v => setGrantDraft(d => ({ ...d, note: v }))} />
          <button type="button" onClick={submitGrant} disabled={granting} style={btnPrimary}>
            {granting ? "…" : "Grant"}
          </button>
        </div>
        {recipients.length === 0 && (
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 8 }}>
            {isSuperAdmin ? "No admin users found yet." : "No end-user broker accounts found yet."}
          </div>
        )}
      </div>

      {/* Wallets table */}
      <div style={tableCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#2c3e50" }}>Wallets</div>
          <button type="button" onClick={fetchWallets} style={btnSecondary} disabled={loading}>
            {loading ? "…" : "Refresh"}
          </button>
        </div>
        <table style={table}>
          <thead>
            <tr>
              {["Username", "Owner Type", "Balance (used / granted framing)", "Low Threshold", "Status", ""].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {wallets.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#888" }}>
                {loading ? "Loading…" : "No wallets yet — grant credits above to create one."}
              </td></tr>
            ) : wallets.map(w => {
              const sm = statusBadge(w.balance, w.low_balance_threshold);
              return (
                <tr key={w.username}>
                  <td style={td}><div style={{ fontWeight: 700, fontSize: 13 }}>{w.username}</div></td>
                  <td style={td}>
                    <span style={{ ...badge, background: "#e9ecef", color: "#495057" }}>{w.wallet_owner_type}</span>
                  </td>
                  <td style={td}>{w.balance}</td>
                  <td style={td}>{w.low_balance_threshold}</td>
                  <td style={td}><span style={{ ...badge, background: sm.bg, color: sm.color }}>{sm.label}</span></td>
                  <td style={td}>
                    <button type="button" onClick={() => openDetail(w.username)} style={btnSmall}>Ledger</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailUsername && (
        <Modal title={`Wallet — ${detailUsername}`} onClose={() => setDetailUsername(null)}>
          {!detail ? (
            <div style={{ color: "#888", fontSize: 13 }}>Loading…</div>
          ) : (
            <div>
              <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
                <StatTile label="Balance" value={detail.balance} color="#2c3e50" />
                <StatTile label="Threshold" value={detail.threshold} color="#6c757d" />
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, color: "#2c3e50", marginBottom: 6 }}>Batches</div>
              <table style={{ ...table, marginBottom: 16 }}>
                <thead>
                  <tr>{["Total", "Remaining", "Granted", "Expires", "Status", "Note"].map(h => <th key={h} style={{ ...th, fontSize: 10, padding: "6px 8px" }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(detail.batches || []).map(b => (
                    <tr key={b.id}>
                      <td style={{ ...td, padding: "6px 8px" }}>{b.credits_total}</td>
                      <td style={{ ...td, padding: "6px 8px" }}>{b.credits_remaining}</td>
                      <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>{b.granted_at}</td>
                      <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>{b.expires_at}</td>
                      <td style={{ ...td, padding: "6px 8px" }}>{b.status}</td>
                      <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>{b.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ fontWeight: 700, fontSize: 12, color: "#2c3e50", marginBottom: 6 }}>Recent Ledger</div>
              <table style={table}>
                <thead>
                  <tr>{["Event", "Δ", "Contract", "Lots", "Counterparty / Ref", "When"].map(h => <th key={h} style={{ ...th, fontSize: 10, padding: "6px 8px" }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(detail.ledger || []).map(l => {
                    // reference_id holds the counterparty username for GRANT
                    // rows (who granted it / who it went to), or a broker fill
                    // id for OPTION_FILL/RECONCILE rows.
                    const refLabel = l.event_type === "GRANT"
                      ? (l.credits_delta < 0 ? `to ${l.reference_id}` : `from ${l.reference_id}`)
                      : [l.reference_type, l.reference_id].filter(Boolean).join(" · ");
                    return (
                      <tr key={l.id}>
                        <td style={{ ...td, padding: "6px 8px" }}>{l.event_type}</td>
                        <td style={{ ...td, padding: "6px 8px", color: l.credits_delta < 0 ? "#dc3545" : "#28a745" }}>{l.credits_delta}</td>
                        <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>
                          {l.symbolname ? `${l.symbolname} ${l.strikeprice}${l.optiontype} ${l.expirydate}` : "—"}
                        </td>
                        <td style={{ ...td, padding: "6px 8px" }}>{l.lots ?? "—"}</td>
                        <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>{refLabel || "—"}</td>
                        <td style={{ ...td, padding: "6px 8px", fontSize: 11 }}>{l.created_at}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div style={tile}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <div style={formLabel}>{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} autoComplete="off" style={formInput} />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawerPanel} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#2c3e50" }}>{title}</span>
          <button type="button" onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <div style={{ padding: "20px 24px 24px", overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 999999, padding: "12px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14, minWidth: 220, maxWidth: 420, minHeight: 20, lineHeight: 1.4, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", boxSizing: "border-box", whiteSpace: "pre-wrap", wordBreak: "break-word" };
const tile         = { background: "#f8f9fa", borderRadius: 10, padding: "10px 16px" };
const tableCard    = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" };
const table        = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th           = { textAlign: "left", padding: "11px 14px", background: "#f8f9fa", borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 11, color: "#6c757d", textTransform: "uppercase" };
const td           = { padding: "10px 14px", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };
const badge        = { display: "inline-block", padding: "3px 9px", borderRadius: 10, fontSize: 11, fontWeight: 600 };
const btnPrimary   = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const btnSmall     = { padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 12 };
const formLabel    = { fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 };
const formInput    = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%", boxSizing: "border-box" };
const overlay      = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1400, display: "flex", justifyContent: "flex-end" };
const drawerPanel  = { background: "#fff", width: "min(640px, 98vw)", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-4px 0 28px rgba(0,0,0,0.18)" };
const modalHeader  = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 };
const closeBtn     = { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999", lineHeight: 1 };
